import * as React from 'react'
import Button from '@doordash/component-button'
import s from './strings'
import styled from 'styled-components'
import { useEffect, useRef, useState } from 'react'
import {
  ResidentialAddress,
  IAddress,
  AUSTRALIA_COUNTRY_CODE,
} from '../../molecules/residential-address'
import {
  Colors,
  Icon,
  InlineChildren,
  Inset,
  Spacing,
  StackChildren,
  Text,
} from '@doordash/design-language'
import { TemplateResponseData } from '../workflow-template/types'
import { FormattedDivider } from '../../../components/atoms/formatted-divider/FormattedDivider'

export const BannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${Spacing.XxxSmall}px;
  background-color: ${Colors.BannerNegativeBackground};
`

const NEW_ADDRESS = {
  country: AUSTRALIA_COUNTRY_CODE,
  streetAddress: '',
  months: 0,
  years: 0,
  state: 'ACT',
  city: '',
  zipCode: '',
  valid: false,
}

type IAddressHistoryProps = {
  /**
   * function to submit all addresses on click
   * of submit button when all form errors have been fixed
   */
  onSubmitStep: (addresses: TemplateResponseData) => void
}

export const AddressHistory = ({ onSubmitStep }: IAddressHistoryProps) => {
  const refs = useRef<Array<HTMLElement>>([])
  const [months, setMonths] = useState<number>(0)
  const [years, setYears] = useState<number>(0)
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [currentAddressId, setCurrentAddressId] = useState<number>(1)
  const [currentAddress, setCurrentAddress] = useState<IAddress>({
    id: 0,
    currentAddress: true,
    ...NEW_ADDRESS,
  })
  const [addresses, setAddresses] = useState<IAddress[]>([
    {
      id: 1,
      currentAddress: false,
      ...NEW_ADDRESS,
    },
  ])

  const addNewAddress = () => {
    const newAddress = {
      id: currentAddressId + 1,
      currentAddress: false,
      ...NEW_ADDRESS,
    }
    setCurrentAddressId(currentAddressId + 1)
    setAddresses(addresses => [...addresses, newAddress])
  }

  const removeAddress = (addressId: number, years: string, months: string) => {
    updateYears(years, '0')
    updateMonths(months, '0')
    setSubmitted(false)
    setAddresses(addresses =>
      addresses.filter(address => address.id !== addressId)
    )
  }

  const updateAddress = (addressId: number, data: IAddress) => {
    if (addressId === 0) {
      setCurrentAddress({
        id: addressId,
        valid: true,
        currentAddress: true,
        ...data,
      })
    } else {
      setAddresses(addresses => {
        const newAddresses = addresses.map(address => {
          if (addressId === address.id) {
            return {
              id: address.id,
              valid: true,
              currentAddress: false,
              ...data,
            }
          }
          return address
        })
        return newAddresses
      })
    }
  }

  const onSubmit = () => {
    setSubmitted(true)
    refs.current.forEach(
      form =>
        form && form.dispatchEvent(new Event('submit', { cancelable: true }))
    )
  }

  const generateAddressResponseData = (addresses: IAddress[]) => {
    const [currentAddress] = addresses
    // TODO(AUSL-1234): don't hardcode the payload
    const templateResponse: TemplateResponseData = {
      INPUT_TYPE_CURRENT_ADDRESS_INPUT: {
        json_data: {
          ...currentAddress,
        }
      },
      INPUT_TYPE_PREVIOUS_ADDRESS_INPUT: {
        json_data: {
          addresses
        }
      }
    }

    return templateResponse
  }

  useEffect(() => {
    const finalAddresses = [currentAddress, ...addresses]
    if (submitted && !finalAddresses.some(add => !add.valid)) {
      onSubmitStep(generateAddressResponseData(finalAddresses))
    }
  }, [addresses, currentAddress, onSubmitStep, submitted, years])

  const updateMonths = (prev: string, curr: string) => {
    const delta = parseInt(curr, 10) - parseInt(prev, 10)
    const newMonths = months + delta
    if (newMonths >= 12) {
      setYears(years => years + 1)
      setMonths(newMonths % 12)
    } else if (newMonths < 0) {
      setYears(years => years - 1)
      setMonths(newMonths + 12)
    } else {
      setMonths(newMonths)
    }
  }

  const updateYears = (prev: string, curr: string) => {
    const delta = parseInt(curr, 10) - parseInt(prev, 10)
    setYears(years => years + delta)
  }

  return (
    <Inset horizontalSize={Inset.Sizes.None}>
      <StackChildren size={StackChildren.Sizes.XLarge}>
        <StackChildren size={StackChildren.Sizes.XxxSmall}>
          <Text
            styles={Text.Styles.Body2}
            color={Colors.ButtonFlatSecondaryForegroundPressed}
          >
            {s.currentValidAustralianAddress()}
          </Text>
        </StackChildren>
        <ResidentialAddress
          updateAddress={updateAddress}
          address={currentAddress}
          isCurrentAddress={true}
          refs={refs}
          updateMonths={updateMonths}
          updateYears={updateYears}
        />
        <FormattedDivider />
        {addresses.map(address => (
          <React.Fragment key={address.id}>
            <ResidentialAddress
              updateAddress={updateAddress}
              address={address}
              removeAddress={removeAddress}
              refs={refs}
              updateMonths={updateMonths}
              updateYears={updateYears}
              isCurrentAddress={false}
            />
            <FormattedDivider />
          </React.Fragment>
        ))}

        <InlineChildren justifyContent="flex-end">
          <Button
            leadingIcon={Icon.Types.AddCircleFill}
            onClick={addNewAddress}
            type={Button.Types.Tertiary}
            isInline={true}
          >
            {s.anotherPreviousAddress()}
          </Button>
        </InlineChildren>
        <StackChildren size={StackChildren.Sizes.XxxSmall}>
          <InlineChildren justifyContent={InlineChildren.Justification.Center}>
            <Text
              styles={Text.Styles.Body2}
              color={Colors.ButtonFlatSecondaryForegroundPressed}
            >
              {s.yearsAndMonthsAdded(years, months)}
            </Text>
          </InlineChildren>
          <Button onClick={onSubmit} isDisabled={years < 5}>
            {s.continue()}
          </Button>
        </StackChildren>
      </StackChildren>
    </Inset>
  )
}
